// react
import React from "react";

// components
import { Avatar } from "@performyard/py-components";

import Chip from "../Chip";
import DeletableChip from "../Chip/templates/DeletableChip";
// styles
import css from "./FilterChips.module.scss";

interface StringFilter {
	id: string;
	type: "string";
	value: string | string[];
	label: string;
}

interface MultipleEmployeesFilter {
	id: string;
	type: "multiple-employees";
	value: { id: string; name: string }[];
	label: string;
}

interface SingleEmployeeFilter {
	id: string;
	type: "single-employee";
	value: { id: string; name: string };
	label: string;
}

interface CustomRenderedFilter {
	id: string;
	type: "customRender";
	value: React.ReactNode;
	label: string;
}

export type Filter = StringFilter | MultipleEmployeesFilter | SingleEmployeeFilter | CustomRenderedFilter;

type FilterID = Filter["id"];

interface BaseProps {
	/**
	 * The label to display above the chips.
	 * @default null
	 */
	label?: string;
	/**
	 * The filters to display. There are three types of filters: string,
	 * multiple-employees, and single-employee.
	 * @default []
	 */
	filters: Filter[];

	/**
	 * ids of any filters that cannot be removed, and will have no delete button.
	 */
	nonDismissibleFilters?: FilterID[];

	/**
	 * These filters have their onDelete callbacks disabled. when the filter has this value
	 */
	disabledFilters?: Filter[];
	/**
	 * The callback to call when a filter is deleted.
	 * @default () => {}
	 * @param filterID The ID of the filter to delete.
	 * @returns void
	 */
	onDeleteFilter?: (filterID: FilterID) => void;
}

const FilterChips = (props: BaseProps) => {
	const { label, filters, onDeleteFilter, disabledFilters = [], nonDismissibleFilters = [] } = props;

	const handleDelete = (filterID) => {
		if (onDeleteFilter && filterID) {
			onDeleteFilter(filterID);
		}
	};

	return (
		<div className={css.main}>
			{label ? (
				<div data-testid="mainLabel" className={css.mainLabel}>
					{label}
				</div>
			) : null}

			{filters.map((filter) => {
				const { id, type, value } = filter;

				let display: string | React.ReactNode | null = null;

				if (type === "string") {
					if (Array.isArray(value)) {
						display = `${value.join(", ")}`;
					} else {
						display = `${value}`;
					}
				} else if (type === "multiple-employees") {
					display = value.map((item) => (
						<Avatar
							id={item.id}
							name={item.name}
							title={item.name}
							dangerouslySetClassName={css.assigneeAvatar}
							key={item.id}
							size={20}
						/>
					));
				} else if (type === "single-employee") {
					display = (
						<span className={css.singleEmployee} data-testid="employee-avatar">
							<Avatar
								id={value.id}
								name={value.name}
								title={value.name}
								dangerouslySetClassName={css.singleEmployeeAvatar}
								key={value.id}
								size={20}
							/>
							<span
								data-testid="employee-name"
								className={`${css.singleEmployeeName} !whitespace-normal break-words py-line-clamp-1`}
							>
								{value.name}
							</span>
						</span>
					);
				} else if (type === "customRender") {
					display = value;
				}

				const renderedValue = (
					<span className={css.chipContent}>
						{filter.label && <span className={css.chipLabel}>{filter.label}:</span>}
						<span className={`min-w-0 !whitespace-normal break-words py-line-clamp-1 ${css.chipDisplay}`}>
							{display}
						</span>
					</span>
				);

				return (
					<div key={id} className={`min-w-0 ${css.filterItem}`}>
						{nonDismissibleFilters.includes(filter.id) ? (
							<Chip label={renderedValue} color="default" />
						) : (
							<DeletableChip
								label={renderedValue}
								color="default"
								onDelete={
									onDeleteFilter &&
									!disabledFilters.some((disabledFilter) => disabledFilter.value === filter.value)
										? () => handleDelete(id)
										: undefined
								}
							/>
						)}
					</div>
				);
			})}
		</div>
	);
};

export default FilterChips;
